import React, { useEffect, useState } from 'react';
import { Button, Form, Row, Col, Spinner, Container, Card } from 'react-bootstrap';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';

const SiteAssignments = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [assignedTeamMembers, setAssignedTeamMembers] = useState('');
  const [checklists, setChecklists] = useState([{ task_template_id: '', frequency: 'monthly', notes: '' }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!siteId) return;

    const fetchData = async () => {
      try {
        const taskRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/ci/task-templates/grouped`);
        console.log(taskRes.data);
        setTaskTemplates(taskRes.data);

        const assignmentsRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/ci/sites/${siteId}/assignments`);

        setAssignedTeamMembers(assignmentsRes.data.team?.join(', ') || '');

        setChecklists((assignmentsRes.data.tasks || []).map(task => ({
          task_template_id: task.task_template_id,
          frequency: task.frequency,
          notes: task.notes || ''
        })));
      } catch (err) {
        console.error(err);
        alert('Failed to load data.');
      }
    };

    fetchData();
  }, [siteId]);

  const handleChecklistChange = (index, field, value) => {
    const updated = [...checklists];
    updated[index][field] = value;
    setChecklists(updated);
  };

  const addChecklist = () => {
    setChecklists([...checklists, { task_template_id: '', frequency: 'monthly', notes: '' }]);
  };

  const removeChecklist = (index) => {
    setChecklists(checklists.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/ci/sites/${siteId}/tasks`, {
        tasks: checklists,
        assigned_team_members: assignedTeamMembers,
      });

      alert('Assignments saved successfully.');
      navigate('/ci/list-clients');
    } catch (err) {
      console.error(err);
      alert('Failed to save assignments');
    } finally {
      setLoading(false);
    }
  };

  const groupedTemplates = taskTemplates.reduce((acc, template) => {
    const group = template.description || 'Other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(template);
    return acc;
  }, {});

  return (
    <Container className="my-4">
      <h2 className="mb-4">Assign Team & Checklists to Site</h2>

      <Card className="mb-4 p-3">
        <Form.Group className="mb-3">
          <Form.Label>Assigned Team Members</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter names separated by commas"
            value={assignedTeamMembers}
            onChange={e => setAssignedTeamMembers(e.target.value)}
          />
          <Form.Text className="text-muted">
            Example: client@sowash.pk
          </Form.Text>
        </Form.Group>
      </Card>

      <Card className="mb-4 p-3">
        <h5>Checklist Tasks</h5>
        {checklists.map((item, idx) => (
          <Row key={idx} className="align-items-end mb-3">
            <Col md={4}>
              <Form.Label>Template</Form.Label>
              <Form.Select
                className="text-dark bg-white"
                value={item.task_template_id}
                onChange={e => handleChecklistChange(idx, 'task_template_id', parseInt(e.target.value))}
              >
                <option value="">Select Task Template</option>
                {taskTemplates.map(tt => (
                  <option key={tt.id} value={tt.id}>
                    {tt.description}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Frequency</Form.Label>
              <Form.Select
                value={item.frequency}
                onChange={e => handleChecklistChange(idx, 'frequency', e.target.value)}
              >
                <option value="daily">Daily</option>
                <option value="alternative">Alternate Days</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>Notes</Form.Label>
              <Form.Control
                value={item.notes}
                onChange={e => handleChecklistChange(idx, 'notes', e.target.value)}
              />
            </Col>
            <Col md={1}>
              <Button variant="danger" onClick={() => removeChecklist(idx)}>✕</Button>
            </Col>
          </Row>
        ))}
        <Button variant="outline-secondary" onClick={addChecklist}>+ Add Checklist</Button>
      </Card>

      <div className="d-flex justify-content-between">
        <Button variant="secondary" onClick={() => navigate(-1)}>Back</Button>
        <Button variant="primary" onClick={handleSave} disabled={loading}>
          {loading ? <Spinner animation="border" size="sm" /> : 'Save Assignments'}
        </Button>
      </div>
    </Container>
  );
};

export default SiteAssignments;
